import { DropdownMenu, Icon, Tooltip, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { FixedPricingLocation } from "@/redux/slices/trip/types";
import { addToast, formatCurrency, kmToUnit } from "@/utils";
import { ZoneRatesEditTableData, ZoneRatesTableData } from "./types";

export const generateColumns = () => {
  const columns: DataTableColumnDef<ZoneRatesTableData>[] = [
    {
      accessorKey: "zonePricingName",
      header: "Zone Pricing Name",
      cell: (props) => props.row.original.zonePricingName,
      size: 300,
      enableSorting: false,
    },
    {
      accessorKey: "referencePoint",
      header: "Reference Point",
      cell: (props) => props.row.original.referencePoint,
      enableSorting: false,
    },
    {
      accessorKey: "coverageRange",
      header: "Coverage Range",
      cell: (props) => props.row.original.coverageRange,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "action",
      header: "",
      cell: (props) => props.row.original.action,
      size: 75,
      enableSorting: false,
    },
  ];

  return columns;
};

export const generateData = (
  zones: FixedPricingLocation[],
  onManage: (zone: FixedPricingLocation) => void,
  onDelete: (zone: FixedPricingLocation) => void
) => {
  const data = zones.map(
    (zone) =>
      ({
        zonePricingName: (
          <div className="flex flex-row items-center gap-x-2">
            <Typography variant="paragraph">{zone.pickupLocation.name}</Typography>
            {!zone.baseInterval && (
              <Tooltip content="No pricing has been set for this zone yet" placement="right">
                <span>
                  <Icon size="md" name="Danger" variant="Bold" className="flex-shrink-0 text-warning" />
                </span>
              </Tooltip>
            )}
          </div>
        ),
        referencePoint: (
          <div className="flex flex-row items-center gap-x-2">
            <Icon size="md" name="Location" variant="Bold" className="flex-shrink-0 text-danger" />
            <Typography variant="paragraph">{zone.pickupLocation.address}</Typography>
          </div>
        ),
        coverageRange: `${kmToUnit(zone.pickupLocation.radius, zone.distanceUnit)} ${zone.distanceUnit}`,
        action: (
          <DropdownMenu
            button={<Icon name="options" isCustom size="lg" className="text-neutral-dark-gray" />}
            position="bottom-start"
            className="!flex max-w-[24px]"
          >
            <DropdownMenu.Item onClick={() => onManage(zone)}>
              <Icon name="ArrowRight" className="mr-2 text-neutral-dark-gray" size="md" />
              <Typography variant="paragraph">Manage Zone Pricing</Typography>
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={() => onDelete(zone)}>
              <Icon name="Trash" className="mr-2 text-neutral-dark-gray" size="md" />
              <Typography variant="paragraph">Delete Zone Pricing</Typography>
            </DropdownMenu.Item>
          </DropdownMenu>
        ),
      } as ZoneRatesTableData)
  );
  return data;
};

export const generateEditColumns = (distanceUnit: string) => {
  const columns: DataTableColumnDef<ZoneRatesEditTableData>[] = [
    {
      accessorKey: "rangeIntervals",
      header: "Range Intervals",
      cell: (props) => props.row.original.rangeIntervals,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "range",
      header: `Range (${distanceUnit})`,
      cell: (props) => props.row.original.range,
      enableSorting: false,
    },
    {
      accessorKey: "baseTripCost",
      header: "Base Trip Cost",
      cell: (props) => props.row.original.baseTripCost,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "managementFee",
      header: () => (
        <div className="flex flex-row items-center gap-2">
          <Typography variant="action">Management Fee (Optional)</Typography>
          <Tooltip content="Management fee is fully paid to the Operator" placement="bottom">
            <span>
              <Icon name="InfoCircle" size="md" />
            </span>
          </Tooltip>
        </div>
      ),
      cell: (props) => props.row.original.managementFee,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "totalTripCost",
      header: "Total Trip Cost",
      cell: (props) => props.row.original.totalTripCost,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "action",
      header: "",
      cell: (props) => props.row.original.action,
      size: 75,
      enableSorting: false,
    },
  ];

  return columns;
};

export const validateRanges = (ranges: number[]): boolean => {
  let rangeError = 0;
  const isIncremental = ranges.slice(1).every((item, i) => {
    const condition = ranges[i] < item;
    if (!condition) rangeError = i + 1;

    return condition;
  });

  if (!isIncremental) {
    addToast("danger", `Unable to save changes. Range ${rangeError} must be incremental over the previous range.`);
    return false;
  }

  return true;
};

export const getManagementCostUnitSymbol = (currencySymbol: string, unit?: string | null) => (unit === "percentage" ? "%" : currencySymbol);

export const getTotalTripCost = (baseTripCost: number, managementFee: number, currency: string, unit?: string | null) =>
  unit === "percentage"
    ? formatCurrency(baseTripCost + baseTripCost * (managementFee / 100), 2, currency, { skipDecimalConversion: true })
    : formatCurrency(baseTripCost + managementFee, 2, currency, { skipDecimalConversion: true });
